"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect } from "react";
import { motion, AnimatePresence, Variants } from "framer-motion";
import { AppButton } from "@/components";
import { CheckCircle, ChevronRight, Lock, X } from "lucide-react";
import { useLenis } from "lenis/react";
import { JobOpeningProps } from "@/lib/types";
import { isDeadlinePassed } from "@/lib/utils/date";
interface JobModalProps {
  isOpen: boolean;
  onClose: () => void;
  job: JobOpeningProps | null;
}
const SMOOTH_EASE = [0.76, 0, 0.24, 1] as const;
// Circular Animation Variants
const modalVariants: Variants = {
  hidden: {
    clipPath: "circle(0% at 50% 50%)",
    opacity: 0,
    scale: 0.92
  },
  visible: {
    clipPath: "circle(150% at 50% 50%)",
    opacity: 1,
    scale: 1,
    transition: {
      duration: 1.0,
      ease: SMOOTH_EASE,
      when: "beforeChildren",
      staggerChildren: 0.2
    }
  },
  exit: {
    clipPath: "circle(0% at 50% 50%)",
    opacity: 0,
    scale: 0.92,
    transition: {
      duration: 1,
      ease: SMOOTH_EASE
    }
  }
};
//  Add staggered animation for children
const itemVariants: Variants = {
  hidden: {
    opacity: 0,
    y: 30
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.2,
      ease: SMOOTH_EASE
    }
  }
};
const JobModal = t0 => {
  const $ = _c(25);
  const {
    isOpen,
    onClose,
    job
  } = t0;
  const lenis = useLenis();
  let t1;
  let t2;
  if ($[0] !== isOpen || $[1] !== lenis) {
    t1 = () => {
      if (isOpen) {
        document.body.style.overflow = "hidden";
        if (lenis) {
          lenis.stop();
        }
      } else {
        document.body.style.overflow = "unset";
        if (lenis) {
          lenis.start();
        }
      }
      return () => {
        document.body.style.overflow = "unset";
        if (lenis) {
          lenis.start();
        }
      };
    };
    t2 = [isOpen, lenis];
    $[0] = isOpen;
    $[1] = lenis;
    $[2] = t1;
    $[3] = t2;
  } else {
    t1 = $[2];
    t2 = $[3];
  }
  useEffect(t1, t2);
  if (!job) {
    return null;
  }
  let t3;
  if ($[4] !== job.deadline) {
    t3 = isDeadlinePassed(job.deadline);
    $[4] = job.deadline;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const closed = t3;
  let t4;
  if ($[6] !== closed || $[7] !== isOpen || $[8] !== job.applyLink || $[9] !== job.benefits || $[10] !== job.deadline || $[11] !== job.description || $[12] !== job.experienceLevel || $[13] !== job.location || $[14] !== job.niceToHave || $[15] !== job.postedAt || $[16] !== job.responsibilities || $[17] !== job.salaryRange || $[18] !== job.skills || $[19] !== job.title || $[20] !== job.type || $[21] !== onClose) {
    t4 = isOpen && <div className="fixed inset-0 z-9999"><motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-accent-foreground/90 backdrop-blur-md" /><div data-lenis-prevent={true} className="absolute inset-0 overflow-y-auto custom-scrollbar p-4 lg:p-10"><div className="min-h-full flex items-center justify-center"><motion.div variants={modalVariants} initial="hidden" animate="visible" exit="exit" className="relative w-full max-w-5xl bg-white rounded-xs shadow-2xl my-auto z-10"><motion.div variants={itemVariants} className="p-8 lg:p-12 pb-6 border-b border-black/5 flex justify-between items-start"><div><span className="text-primary uppercase text-xs tracking-widest font-black flex items-center gap-2 bg-black px-2 py-1 rounded-xs max-w-32"><span className="w-2 h-2 rounded-full bg-primary shrink-0" />{job.type}</span><h2 className="text-black  text-2xl md:text-4xl lg:text-5xl font-black mt-3 tracking-tighter leading-[1.1]">{job.title}</h2><p className="text-black/40 text-sm mt-2">Posted on {job.postedAt} • {job.location}</p></div><button onClick={onClose} className="p-2 bg-black/5 hover:bg-black/10 rounded-xs transition-all hover:rotate-90 cursor-pointer"><X className="text-black text-lg md:text-2xl" /></button></motion.div><motion.div variants={itemVariants} className="px-8 lg:px-12 py-6 bg-primary/5 border-b border-black/5 flex flex-wrap items-center justify-between gap-8"><div className="flex flex-col md:flex-row md:items-center gap-8 lg:gap-16"><div><h4 className="text-black/30 text-[10px] font-bold uppercase tracking-widest">SALARY RANGE</h4><p className="text-black text-lg font-bold mt-1">{job.salaryRange}</p></div><div><h4 className="text-black/30 text-[10px] font-bold uppercase tracking-widest">LEVEL</h4><p className="text-black text-lg font-bold mt-1">{job.experienceLevel}</p></div>{job.deadline && <div><h4 className="text-black/30 text-[10px] font-bold uppercase tracking-widest">DEADLINE</h4><p className={`text-lg font-bold mt-1 ${closed ? "text-red-500" : "text-black"}`}>{job.deadline}</p></div>}</div>{closed ? <div className="flex flex-col items-start gap-1.5"><span className="inline-flex h-14 items-center gap-2.5 rounded-xs border-2 border-red-500/30 bg-red-500/10 px-5 text-sm font-black uppercase tracking-wider text-red-500"><Lock className="size-4 shrink-0" />Applications Closed</span><span className="text-black/40 text-xs">This role closed on {job.deadline}.</span></div> : <AppButton href={job.applyLink} className="w-52" newTab={true} buttonClass="h-14 font-black">Apply for Role</AppButton>}</motion.div><motion.div variants={itemVariants} className="p-8 lg:p-12 space-y-12 pb-20"><div><h4 className="text-black font-black text-2xl mb-5 flex items-center gap-3">Role Overview</h4><p className="text-black/70 text-lg leading-relaxed max-w-3xl">{job.description}</p></div><div><h4 className="text-black font-black text-2xl mb-6">Key Responsibilities</h4><div className="grid grid-cols-1 md:grid-cols-2 gap-4">{job.responsibilities.map(_temp)}</div></div><div className="grid grid-cols-1 lg:grid-cols-2 gap-10"><div className="space-y-6"><h4 className="text-black font-black text-2xl">Required Expertise</h4><div className="flex flex-wrap gap-2">{job.skills.map(_temp2)}</div></div><div className="space-y-6"><h4 className="text-black font-black text-2xl">Nice to Have</h4><div className="flex flex-wrap gap-2">{job.niceToHave.map(_temp3)}</div></div></div><div className="pt-6"><h4 className="text-black font-black text-2xl mb-6">Company Benefits</h4><div className="grid grid-cols-1 md:grid-cols-3 gap-4">{job.benefits.map(_temp4)}</div></div></motion.div></motion.div></div></div></div>;
    $[6] = closed;
    $[7] = isOpen;
    $[8] = job.applyLink;
    $[9] = job.benefits;
    $[10] = job.deadline;
    $[11] = job.description;
    $[12] = job.experienceLevel;
    $[13] = job.location;
    $[14] = job.niceToHave;
    $[15] = job.postedAt;
    $[16] = job.responsibilities;
    $[17] = job.salaryRange;
    $[18] = job.skills;
    $[19] = job.title;
    $[20] = job.type;
    $[21] = onClose;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  let t5;
  if ($[23] !== t4) {
    t5 = <AnimatePresence>{t4}</AnimatePresence>;
    $[23] = t4;
    $[24] = t5;
  } else {
    t5 = $[24];
  }
  return t5;
};
export default JobModal;
function _temp(res, i) {
  return <div key={i} className="flex gap-3 text-black/60 p-4 border border-black/5 rounded-xs hover:bg-black/2 transition-colors"><CheckCircle className="text-primary mt-1 shrink-0" /><span className="text-base">{res}</span></div>;
}
function _temp2(skill) {
  return <span key={skill} className="px-4 py-2 bg-primary text-black text-xs font-black rounded-xs uppercase tracking-wider">{skill}</span>;
}
function _temp3(item) {
  return <span key={item} className="px-4 py-2 border-2 border-accent-foreground/20 text-black/60 text-xs font-bold rounded-xs uppercase tracking-wider">{item}</span>;
}
function _temp4(benefit) {
  return <div key={benefit} className="flex items-center gap-3 bg-primary/5 p-4 rounded-xs group hover:bg-primary transition-all duration-300"><ChevronRight className="text-primary group-hover:text-black transition-colors" /><span className="text-black font-bold text-xs capitalize tracking-tight">{benefit}</span></div>;
}